import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, from, empty } from 'rxjs';
import { ListingsService } from '../services/listings.service';
import { Listing } from '../classes/listing';
import { PreRenderService } from '../services/pre-render.service';
import { CommunicationService } from '../services/communication.service';
import { ListingCategory } from '../classes/enums';
import { catchError, map } from 'rxjs/operators';
import { Commercial, QuebecStateShort} from '../app-config.constants';
import { AnalyticsService } from '../services/analytics/analytics.service';
import { OFFMARKET_ACTIVE_LISTING, OFFMARKET_OFFMARKET_PROPERTY } from '../classes/analytics-tags';
import { UrlSanitizerService } from '../services/url-sanitizer.service';
import { UrlValidator } from './url-validator';

@Injectable()
export class OffMarketListingResolver extends UrlValidator implements Resolve<Listing> {

  constructor(
    private _listingsService: ListingsService,
    private _analyticsService: AnalyticsService,
    private _communicationService: CommunicationService,
    protected _urlSanitizerService: UrlSanitizerService,
    protected _preRenderService: PreRenderService,
    protected _router: Router
  ) {
    super(_urlSanitizerService, _router, _preRenderService);
  }

  resolve(route: ActivatedRouteSnapshot, rstate: RouterStateSnapshot): Observable<Listing> {
    const url = rstate.root.firstChild.url;

    let listingIdWithBoardPrefix = '0';
    const listingIdRegex = /-(\w+_\w+)-prty/;

    const pathToBeChecked = url.length === 4 ? url[3].path : url[2].path;

    const isCommercial = url[0].path === Commercial;
    const isQuebec = url[0].path === QuebecStateShort.toLowerCase() || url[1].path === QuebecStateShort.toLowerCase();
    const listingCategory = isCommercial ? ListingCategory.COMMERCIAL : ListingCategory.RESIDENTIAL;

    this._communicationService.setCurrentListingType(listingCategory);

    const listingIdMatches = listingIdRegex.exec(pathToBeChecked);

    if (listingIdMatches && listingIdMatches.length === 2) listingIdWithBoardPrefix = listingIdMatches[1];

    if (!this.isValidUrl(url)) return empty();

    return from(this._listingsService.getListingDetails(listingIdWithBoardPrefix)).pipe(
      map((listing: Listing) => {

        this._communicationService.setCurrentListingType(this.getListingCategory(listing));
        if (listing.isOffMarket) {
          this._analyticsService.eventTrack(OFFMARKET_OFFMARKET_PROPERTY, null);
        } else {
          this._analyticsService.eventTrack(OFFMARKET_ACTIVE_LISTING, null);
        }
        return listing;
      }),
      catchError((err) => {
        return this.redirectOnError(url, isQuebec, isCommercial);
      }));
  }

  private redirectOnError(url, isQuebec, isCommercial) {
    let fallbackUrl = url.length === 4 ? url[0] + '/' + url[1] + '/' + url[2] : url[0] + '/' + url[1];

    // MBL-2919 - Removed redirect temporarily until MBL - 2900 completed
    if (isQuebec) fallbackUrl = isCommercial ?  `/${Commercial}` : '/';

    this.navigationRedirect(fallbackUrl);
    return empty();
  }

  private getListingCategory(listing: Listing) {
    if (listing.isLuxury) {
      return ListingCategory.LUXURY;
    }
    else if (listing.isCommercial) {
      return ListingCategory.COMMERCIAL;
    }
    return this._communicationService.listings.currentShownListingType.getValue();
  }
}
